/**
 * OpportunitiesPage v2 — Dashboard dinámico de Oportunidades Comerciales.
 *
 * Reemplaza el dashboard estático original (KPIs + 2 charts + 3 insights + recs
 * fijos) por una grilla de widgets que el usuario puede crear, editar y
 * regenerar individualmente. Cada widget tiene su propio prompt + dataset y se
 * regenera por separado contra Bedrock — un fallo no tira los demás.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  FaLightbulb, FaSync, FaFilePdf, FaPlus, FaEdit, FaCheck,
  FaSpinner, FaExclamationTriangle, FaMagic,
} from 'react-icons/fa';

import { useDashboard } from '../../hooks/useDashboard';
import { formatReportDate } from '../../services/dashboardsService';
import useActivityTracker from '../../hooks/useActivityTracker';

import WidgetCard from './widgets/WidgetCard';
import WidgetEditorModal from './widgets/WidgetEditorModal';

import './OpportunitiesPage.css';

const OpportunitiesPage = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const username = user?.username;
  const { trackAction } = useActivityTracker(username, 'opportunities');

  const {
    dashboard, widgets, meta,
    isLoading, isReady, hasError, error,
    refreshingAll, isWidgetGenerating,
    refreshAll, refreshWidget,
    addWidget, editWidget, removeWidget,
    reorder,
    suggestWidget,
    clearError,
  } = useDashboard(username);

  const [editMode, setEditMode] = useState(false);
  const [editorState, setEditorState] = useState({
    isOpen: false,
    mode: 'create',    // 'create' | 'edit'
    initialWidget: null,
    busy: false,
  });

  // Estado del input "describir widget con IA"
  const [aiDescription, setAiDescription] = useState('');
  const [aiThinking, setAiThinking] = useState(false);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const openCreateModal = useCallback(() => {
    setEditorState({ isOpen: true, mode: 'create', initialWidget: null, busy: false });
  }, []);

  const openEditModal = useCallback((widget) => {
    setEditorState({ isOpen: true, mode: 'edit', initialWidget: widget, busy: false });
  }, []);

  const closeModal = useCallback(() => {
    setEditorState((s) => s.busy ? s : { ...s, isOpen: false });
  }, []);

  const handleSubmit = useCallback(async (payload) => {
    setEditorState((s) => ({ ...s, busy: true }));
    const editingId = editorState.initialWidget?.id;
    try {
      if (editorState.mode === 'create') {
        // Crear vacío (rápido) y cerrar el modal de inmediato. El usuario ve
        // la card aparecer en la grid con su loading state mientras el agente
        // trabaja en background.
        const newWidget = await addWidget(payload, { generate: false });
        setEditorState({ isOpen: false, mode: 'create', initialWidget: null, busy: false });
        if (newWidget?.id) {
          // No await: corre en background, el spinner del widget se actualiza solo.
          refreshWidget(newWidget.id, { mode: 'full' });
        }
      } else if (editingId) {
        await editWidget(editingId, payload);
        setEditorState({ isOpen: false, mode: 'create', initialWidget: null, busy: false });
        // Al editar prompt/tabla/tipo, los datos guardados quedan obsoletos →
        // disparamos regeneración completa en background.
        refreshWidget(editingId, { mode: 'full' });
      }
    } catch (err) {
      console.error('[OpportunitiesPage] submit failed:', err);
      setEditorState((s) => ({ ...s, busy: false }));
    }
  }, [editorState, addWidget, editWidget, refreshWidget]);

  const handleDelete = useCallback(async (widget) => {
    const ok = window.confirm(`¿Eliminar el widget "${widget.title || 'sin título'}"?`);
    if (!ok) return;
    await removeWidget(widget.id);
  }, [removeWidget]);

  // Reordenamiento: swap del widget en `index` con el de `index + delta`.
  // `delta = -1` mueve hacia arriba (antes), `+1` hacia abajo (después).
  const moveWidget = useCallback((index, delta) => {
    if (!widgets.length) return;
    const sorted = [...widgets].sort((a, b) => a.position - b.position || a.id - b.id);
    const target = index + delta;
    if (target < 0 || target >= sorted.length) return;
    const ids = sorted.map(w => w.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorder(ids);
  }, [widgets, reorder]);

  // -----------------------------------------------------------------------
  // Sugerencia de widget con IA: usuario describe → IA propone → abrimos
  // modal pre-llenado para que confirme/ajuste.
  // -----------------------------------------------------------------------
  const handleAiSuggest = useCallback(async () => {
    const text = aiDescription.trim();
    if (!text) return;
    setAiThinking(true);
    try {
      const suggestion = await suggestWidget(text);
      if (!suggestion) return;  // El hook ya seteó el error
      // Abrimos el modal en modo 'create' con los campos pre-llenados.
      // El title del modal sigue diciendo "Nuevo widget" (es uno nuevo).
      setEditorState({
        isOpen: true,
        mode: 'create',
        initialWidget: {
          widget_type: suggestion.widget_type,
          chart_type: suggestion.chart_type,
          dataset: suggestion.dataset || '',
          size: suggestion.size,
          title: suggestion.title,
          prompt: suggestion.prompt,
        },
        busy: false,
      });
      setAiDescription('');
    } finally {
      setAiThinking(false);
    }
  }, [aiDescription, suggestWidget]);

  const handleAiKeyDown = useCallback((e) => {
    // Enter (sin Shift) dispara el submit; Shift+Enter agrega línea.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAiSuggest();
    }
  }, [handleAiSuggest]);

  const handleExportPDF = () => {
    trackAction('export_pdf');
    window.print();
  };

  // Toggle de modo edición con tracking
  const toggleEditMode = useCallback(() => {
    setEditMode((prev) => {
      const next = !prev;
      trackAction(next ? 'enter_edit_mode' : 'exit_edit_mode');
      return next;
    });
  }, [trackAction]);

  // Widgets ordenados por position (defensivo, ya viene ordenado del backend)
  const orderedWidgets = useMemo(
    () => [...widgets].sort((a, b) => a.position - b.position || a.id - b.id),
    [widgets]
  );

  // "Última actualización" = la vez más reciente que se regeneró algún widget
  // (widget.last_generated_at, que SÍ se actualiza en cada refresh), NO el
  // dashboard.updated_at (que solo cambia al crear/renombrar el dashboard y por
  // eso quedaba congelado en la fecha de creación aunque los datos se
  // actualizaran). Fallback a dashboard.updated_at si ningún widget se generó aún.
  const lastDataUpdate = useMemo(() => {
    const times = widgets
      .map((w) => w.last_generated_at)
      .filter(Boolean)
      .map((t) => new Date(t).getTime())
      .filter((n) => !Number.isNaN(n));
    if (times.length) return new Date(Math.max(...times)).toISOString();
    return dashboard?.updated_at || null;
  }, [widgets, dashboard]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (isLoading) {
    return (
      <main className="opportunities-page">
        <div className="opportunities-loading">
          <FaSpinner className="spin" size={48} />
          <p>Cargando tu dashboard…</p>
        </div>
      </main>
    );
  }

  if (hasError && !dashboard) {
    return (
      <main className="opportunities-page">
        <div className="opportunities-error">
          <FaExclamationTriangle size={48} />
          <h3>No pudimos cargar el dashboard</h3>
          <p>{error || 'Error desconocido'}</p>
          <button type="button" onClick={() => window.location.reload()}>
            Recargar
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="opportunities-page">
      {/* ============ HEADER ============ */}
      <div className="opportunities-header">
        <div className="header-left">
          <h1>
            <FaLightbulb className="header-icon" />
            {dashboard?.name || 'Oportunidades Comerciales'}
          </h1>
          {lastDataUpdate && (
            <span className="last-update">
              Actualizado: {formatReportDate(lastDataUpdate)}
            </span>
          )}
        </div>
        <div className="header-actions">
          <button
            type="button"
            className={`btn-toggle-edit ${editMode ? 'active' : ''}`}
            onClick={toggleEditMode}
            title={editMode ? 'Salir del modo edición' : 'Editar dashboard'}
          >
            {editMode ? <><FaCheck /> Listo</> : <><FaEdit /> Editar</>}
          </button>
          <button
            type="button"
            className="btn-refresh"
            onClick={() => refreshAll({ mode: 'fast' })}
            disabled={refreshingAll || !isReady}
            title="Trae los últimos datos sin volver a rehacer los widgets"
          >
            {refreshingAll ? (
              <><FaSpinner className="spin" /> Actualizando…</>
            ) : (
              <><FaSync /> Actualizar todo</>
            )}
          </button>
          <button
            type="button"
            className="btn-export"
            onClick={handleExportPDF}
            title="Exportar a PDF"
          >
            <FaFilePdf /> PDF
          </button>
        </div>
      </div>

      {/* ============ ERROR BAR ============ */}
      {error && (
        <div className="opportunities-error-bar">
          <FaExclamationTriangle />
          <span>{error}</span>
          <button type="button" onClick={clearError}>Cerrar</button>
        </div>
      )}

      {/* ============ EDIT MODE TOOLBAR ============ */}
      {editMode && (
        <div className="opportunities-edit-toolbar">
          <div className="ai-widget-creator">
            <FaMagic className="ai-widget-icon" />
            <input
              type="text"
              className="ai-widget-input"
              placeholder="Describí qué widget querés y lo armo… (ej: 'productos con mayor subida de precio')"
              value={aiDescription}
              onChange={(e) => setAiDescription(e.target.value)}
              onKeyDown={handleAiKeyDown}
              disabled={aiThinking}
              maxLength={500}
            />
            <button
              type="button"
              className="btn-ai-create"
              onClick={handleAiSuggest}
              disabled={aiThinking || !aiDescription.trim()}
            >
              {aiThinking ? <><FaSpinner className="spin" /> Pensando…</> : <>Crear</>}
            </button>
          </div>
          <div className="edit-toolbar-secondary">
            <button
              type="button"
              className="btn-add-manual"
              onClick={openCreateModal}
              disabled={aiThinking}
              title="Crear un widget configurando todo manualmente"
            >
              <FaPlus /> Agregar manual
            </button>
          </div>
        </div>
      )}

      {/* ============ GRID DE WIDGETS ============ */}
      {orderedWidgets.length === 0 ? (
        <div className="opportunities-empty">
          <FaLightbulb size={48} />
          <h3>Aún no tienes widgets</h3>
          <p>Activa el modo edición y agrega tu primer widget para empezar.</p>
          <button type="button" className="btn-add-widget" onClick={() => { setEditMode(true); openCreateModal(); }}>
            <FaPlus /> Crear mi primer widget
          </button>
        </div>
      ) : (
        <div className="widgets-grid">
          {orderedWidgets.map((w, idx) => (
            <WidgetCard
              key={w.id}
              widget={w}
              editMode={editMode}
              isGenerating={isWidgetGenerating(w.id)}
              canMoveUp={idx > 0}
              canMoveDown={idx < orderedWidgets.length - 1}
              onRefresh={() => refreshWidget(w.id)}
              onEdit={() => openEditModal(w)}
              onDelete={() => handleDelete(w)}
              onMoveUp={() => moveWidget(idx, -1)}
              onMoveDown={() => moveWidget(idx, +1)}
            />
          ))}
        </div>
      )}

      {/* ============ MODAL DE EDICIÓN ============ */}
      <WidgetEditorModal
        isOpen={editorState.isOpen}
        mode={editorState.mode}
        initialWidget={editorState.initialWidget}
        meta={meta}
        username={username}
        onClose={closeModal}
        onSubmit={handleSubmit}
        busy={editorState.busy}
      />
    </main>
  );
};

export default OpportunitiesPage;
